import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Card, Icon, Section } from "@/components/primitives";

// Community guidelines — short, scannable, design-system styled. Linked from
// Settings → Tips → Community guidelines & Contact support.

interface Rule {
  title: string;
  body: string;
}

const RULES: Rule[] = [
  {
    title: "No objectionable content",
    body: "Hate speech, harassment, sexually explicit material involving minors, threats of violence, or illegal-activity promotion are grounds for removal.",
  },
  {
    title: "No counterfeit promotion",
    body: "Posting counterfeits as authentic is grounds for a permanent ban. FIG is built on trust — fake-as-real undermines every collector here.",
  },
  {
    title: "Respect other users",
    body: "Disagreements happen. Personal attacks don't. Use the Block and Report tools liberally — they're on every post, comment, message, and profile.",
  },
  {
    title: "One account per person",
    body: "No spam, no automation, no fake personas. Multiple accounts to manipulate follows, votes, or feedback will be removed.",
  },
  {
    title: "We act fast",
    body: "Reports are reviewed within 24 hours. Severe violations (counterfeit fraud, threats, CSAM) are escalated immediately and result in permanent removal.",
  },
];

export default function CommunityGuidelines() {
  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
            aria-label="Back"
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Community</span>
          <div style={{ width: 30 }} />
        </div>

        <Section pad={14} eyebrow="Rules of the house">
          <div style={{
            background: "hsl(var(--ink))",
            color: "hsl(var(--ink-fg))",
            padding: "20px 22px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
              fontSize: 30, lineHeight: 1.05, letterSpacing: "-0.02em",
            }}>
              Community guidelines.
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontStyle: "italic",
              fontSize: 14, opacity: 0.85,
              marginTop: 8, lineHeight: 1.4,
            }}>
              FIG is a small community built on trust between collectors.
              These five rules keep it that way.
            </div>
          </div>
        </Section>

        <Section pad={14}>
          {RULES.map((rule, i) => (
            <Card key={i} style={{ padding: 14, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, letterSpacing: "0.12em",
                  color: "hsl(var(--rust))",
                  fontWeight: 700,
                }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 800, fontSize: 16, color: "hsl(var(--ink))",
                  lineHeight: 1.25,
                }}>
                  {rule.title}
                </div>
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, color: "hsl(var(--ink-2))",
                lineHeight: 1.5,
                paddingLeft: 26,
              }}>
                {rule.body}
              </div>
            </Card>
          ))}

          <div style={{
            marginTop: 14,
            padding: "14px 16px",
            background: "hsl(var(--cream))",
            border: "1px dashed hsl(var(--sand-deep))",
          }}>
            <div className="caps" style={{
              color: "hsl(var(--ink-2))", marginBottom: 6,
            }}>
              Reporting & questions
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5,
            }}>
              Reporting and blocking tools live on every post, comment,
              message, and profile. For anything that needs a human, email{" "}
              <a
                href="mailto:support@figverified.com"
                style={{ color: "hsl(var(--rust))", fontWeight: 600 }}
              >
                support@figverified.com
              </a>.
            </div>
          </div>
        </Section>
      </div>
    </MobileFrame>
  );
}
